import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffectOnce } from 'usehooks-ts';
import { theme } from '@xbto/design-system';
import { monthsList } from 'common';
import { Button, Col, Icon, Row, TextBody, TextTitle } from '../../atoms';
import { FundTableLayout } from '../fund-table-layout';
import Select from '../../molecules/select/component';
import { ErrorCallout } from '../../organisms';
export default function ComponentWeb({ rowData, error, years = [], onYearChange, onDownloadLatestPress, onFactSheetDownloadPress, }) {
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    useEffectOnce(() => {
        if (rowData && rowData.length > 0) {
            const latestYear = Math.max(...rowData.map(item => item.year));
            setSelectedYear(latestYear);
        }
    });
    const yearOptions = useMemo(() => rowData && (years === null || years === void 0 ? void 0 : years.length)
        ? years === null || years === void 0 ? void 0 : years.map(item => ({ label: `${item}`, value: `${item}` }))
        : [{ label: currentYear.toString(), value: currentYear.toString() }], [currentYear, rowData, years]);
    const handleYearChange = useCallback((y) => {
        const v = Number(y);
        setSelectedYear(v);
        onYearChange(v);
    }, [onYearChange]);
    const handleFactSheetDownloadPress = useCallback((month, year) => () => {
        onFactSheetDownloadPress(month, year);
    }, [onFactSheetDownloadPress]);
    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('month', {
            cell: info => {
                const val = info.getValue();
                return _jsxs(TextBody, { em: true, children: [monthsList[val - 1].name, " Factsheet"] });
            },
            size: 232,
            header: 'Month',
            meta: {
                height: 52,
                justify: 'flex-start',
                cursor: 'initial',
                autoWidth: true,
                paddingSmall: true,
            },
        }),
        columnHelper.accessor('year', {
            cell: ({ row }) => {
                const triggerDownload = handleFactSheetDownloadPress(row.original.month, row.original.year);
                return (_jsx("div", { role: "button", tabIndex: 0, onClick: triggerDownload, onKeyDown: e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            triggerDownload();
                        }
                    }, children: _jsx(Icon, { name: "FileDownload", size: 20, color: "primary" }) }));
            },
            size: 78,
            header: 'Download',
            meta: {
                height: 52,
                justify: 'flex-end',
                cursor: 'initial',
                paddingSmall: true,
            },
        }),
    ], [columnHelper, handleFactSheetDownloadPress]);
    return (_jsx(FundTableLayout, { title: _jsxs(Col.L, { children: [_jsxs(Row.LR, { style: { width: '100%' }, children: [_jsx(TextTitle, { small: true, children: "Factsheets" }), _jsx(Button, { variant: "tertiary", size: "sm", label: "Download latest factsheet", onPress: onDownloadLatestPress, disabled: !!error })] }), !error && (_jsx(Col, { style: {
                        zIndex: 2,
                        marginTop: theme.spacing['6'],
                        width: 'auto',
                        marginBottom: -theme.spacing['2'],
                    }, children: _jsx(Select, { value: `${selectedYear}`, onChange: handleYearChange, options: yearOptions, placeholder: "Select year", buttonSize: "sm" }) }))] }), subtitle: null, data: rowData || [], error: error, errorFallback: _jsx(ErrorCallout, { title: "Unable to display factsheets", message: "We are unable to display factsheets at the moment, please try again later", height: 204 }), columns: columns, noHeader: true, hideOnNoData: true, flatBorders: true }));
}
